import { Icon, type IconProps } from "@design-system/Icon";
import { Chip, ChipProps } from "@design-system/Inputs/Chip";
import { useSelect, type SelectProps } from "@design-system/internal/select";
import { Box, type BoxProps } from "@design-system/Layout/Box";
import { FlexRow } from "@design-system/Layout/Flex";
import { Text, type TextProps } from "@design-system/Typography/Text";
import { cn, useControllableState } from "@design-system/Utilities";
import * as Popover from "@radix-ui/react-popover";
import {
    createContext,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    type FC,
    type HTMLAttributes,
    type KeyboardEvent,
    type PointerEvent,
    type PropsWithChildren,
    type ReactNode,
    type Ref,
} from "react";
import { Button } from "../Button";
import { Checkbox, type CheckboxProps } from "../Checkbox";
import { useUniversalDropdownTranslations } from "./i18n";
import {
    useDropdownContentStyles,
    useDropdownItemIconStyles,
    useDropdownItemStyles,
    useDropdownItemTextStyles,
} from "./styles";

/* -------------------------------------------------------------------------------------------------
 * Dropdown Context
 * -----------------------------------------------------------------------------------------------*/
type DropdownCtxType = {
    value?: string[];
    handleSelect?: (value: string) => void;
    onReset?: () => void;
};

const DropdownCtx = createContext<DropdownCtxType | null>(null);
const useDropdownCtx = () => {
    const ctx = useContext(DropdownCtx);
    if (!ctx)
        throw new Error(
            "useDropdownCtx must be used within a DropdownCtx provider",
        );
    return ctx;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Item Context
 * -----------------------------------------------------------------------------------------------*/
type DropdownItemCtxType = {
    selected: boolean;
    negative?: boolean;
};

const DropdownItemCtx = createContext<DropdownItemCtxType | null>(null);
const useDropdownItemCtx = () => {
    const ctx = useContext(DropdownItemCtx);
    if (!ctx)
        throw new Error(
            "useDropdownItemCtx must be used within a DropdownItemCtx provider",
        );
    return ctx;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Item Base
 * -----------------------------------------------------------------------------------------------*/
type DropdownItemProps = BoxProps & { active?: boolean; variant?: "negative" };
const DropdownItem: FC<DropdownItemProps & { ref?: Ref<HTMLDivElement> }> = ({
    active,
    variant,
    ref,
    ...props
}) => {
    const className = useDropdownItemStyles(
        { active, negative: variant === "negative" },
        props.className,
    );
    return <Box ref={ref} tabIndex={1} {...props} className={className} />;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Selectable Item
 * -----------------------------------------------------------------------------------------------*/
const DropdownSelectableItem: FC<
    DropdownItemProps & { ref?: Ref<HTMLDivElement>; value: string }
> = ({ children, onKeyDown, onClick, value, variant, ref, ...props }) => {
    const { handleSelect, value: selectedValue } = useDropdownCtx();
    const isSelected = !!selectedValue?.includes(value);
    const active =
        typeof props.active === "boolean" ? props.active : isSelected;

    const handleClick = useCallback(
        (evt: PointerEvent<HTMLDivElement>) => {
            handleSelect?.(value);
            onClick?.(evt);
        },
        [handleSelect, value, onClick],
    );

    const handleKeyDown = (evt: KeyboardEvent<HTMLDivElement>) => {
        if (evt.key === "Enter" || evt.key === " ") {
            handleSelect?.(value);
        }
        return onKeyDown?.(evt);
    };

    return (
        <DropdownItemCtx.Provider
            value={{
                selected: isSelected,
                negative: variant === "negative",
            }}
        >
            <DropdownItem
                {...props}
                active={active}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                ref={ref}
                variant={variant}
            >
                {children}
            </DropdownItem>
        </DropdownItemCtx.Provider>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Reset Item
 * -----------------------------------------------------------------------------------------------*/
type DropdownResetItemProps = HTMLAttributes<HTMLDivElement> & {
    label: string;
    ref?: Ref<HTMLDivElement>;
};
const DropdownResetItem: FC<DropdownResetItemProps> = ({
    className,
    label,
    ref,
    ...props
}) => {
    const { onReset } = useDropdownCtx();
    const itemClassName = useDropdownItemStyles({}, className);
    const { t, keys } = useUniversalDropdownTranslations();

    return (
        <FlexRow
            onClick={onReset}
            className={itemClassName}
            ref={ref}
            {...props}
        >
            <Icon name="close" size="sm" />
            <Text>{label ?? t(keys.reset)}</Text>
        </FlexRow>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Item Text
 * -----------------------------------------------------------------------------------------------*/
const DropdownItemText: FC<TextProps & { ref?: Ref<HTMLDivElement> }> = ({
    className: propsClassName,
    ref,
    ...props
}) => {
    const className = useDropdownItemTextStyles(propsClassName);
    return <Text ref={ref} {...props} className={className} />;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Item Icon
 * -----------------------------------------------------------------------------------------------*/
const DropdownItemIcon: FC<IconProps & { ref?: Ref<SVGSVGElement> }> = ({
    className: propsClassName,
    ref,
    ...props
}) => {
    const className = useDropdownItemIconStyles(propsClassName);
    return <Icon ref={ref} {...props} className={className} />;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Item Single Select Indicator
 * -----------------------------------------------------------------------------------------------*/
const DropdownItemSelectedIndicator = () => {
    const { selected } = useDropdownItemCtx();
    return (
        <Box className="grid place-content-center">
            {selected ? <Icon className="text-brand" name="check" /> : null}
        </Box>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Checkbox
 * -----------------------------------------------------------------------------------------------*/
const DropdownCheckbox: FC<
    CheckboxProps & { ref?: Ref<HTMLButtonElement> }
> = ({ ref, ...props }) => {
    const { selected } = useDropdownItemCtx();
    return <Checkbox checked={selected} {...props} ref={ref} />;
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Anchor
 * -----------------------------------------------------------------------------------------------*/
const DropdownAnchor = Popover.Anchor;

/* -------------------------------------------------------------------------------------------------
 * Dropdown Content
 * -----------------------------------------------------------------------------------------------*/

type DropdownContentProps = PropsWithChildren<Popover.PopoverContentProps> & {
    hasPortal?: boolean;
};

const DropdownContent: FC<
    DropdownContentProps & { ref?: Ref<HTMLDivElement> }
> = ({
    className: propsClassName,
    hasPortal,
    sideOffset = 4,
    ref,
    ...props
}) => {
    const Wrapper = hasPortal ? Popover.Portal : Fragment;
    const className = useDropdownContentStyles(propsClassName);

    return (
        <Wrapper>
            <Popover.Content
                ref={ref}
                {...props}
                className={className}
                sideOffset={sideOffset}
                tabIndex={0}
            />
        </Wrapper>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Trigger
 * -----------------------------------------------------------------------------------------------*/
type DropdownTriggerProps = Popover.PopoverTriggerProps & {
    label?: string;
    ref?: Ref<HTMLButtonElement>;
};
const DropdownTrigger: FC<DropdownTriggerProps> = ({
    asChild,
    children,
    label,
    ref,
    ...props
}) => {
    const { value } = useDropdownCtx();
    const hasValue = !!value && !!value.length;
    return (
        <Popover.Trigger
            asChild={children ? asChild : true}
            ref={ref}
            {...props}
        >
            {children ?? (
                <Button active={hasValue} size="sm" variant="outlined">
                    {label}
                    <Button.Icon
                        className={cn("text-primary", hasValue && "text-brand")}
                        name="chevronDown"
                    />
                </Button>
            )}
        </Popover.Trigger>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown Chip Trigger
 * -----------------------------------------------------------------------------------------------*/

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Option = Record<string, any>;
type DropdownChipTriggerProps<O extends Option> = Omit<
    DropdownTriggerProps,
    "asChild"
> &
    Omit<ChipProps, "active"> & {
        /**
         * Any icon, avatar, or image to display before the label.
         *
         * @example
         * leadingIcon = <Icon name="filter" />
         */
        leadingElement?: ReactNode;
        /**
         * The label to display in the chip.
         *
         * @example
         * label = "Options"
         * Then the chip will display "Options" when nothing is selected,
         * and "Options: Option 1, +1" when an option is selected.
         * The colon is automatically added when some values are selected.
         */
        label?: string;
        /**
         * The options to display in the dropdown.
         * They are used to extract the value to display in the chip based
         * on the selected values of the dropdown.
         *
         * @example
         * options = [{ id: "1", name: "Option 1" }, { id: "2", name: "Option 2" }]
         */
        options?: O[];
        /**
         * The name of the property on each option object that holds its unique identifier.
         * This identifier is used to match the selected value(s) from the Dropdown context
         * with the corresponding object in the `options` array. The value accessed via this key
         * (e.g., `option[valueIdentifierKey]`) should be a string and match the type of values stored
         * in the Dropdown's selection state.
         *
         * @default "id"
         * @example valueIdentifierKey="productId"
         * @example // Given options: [{ value: 'abc', label: 'Item 1' }]
         * valueIdentifierKey="value"
         */
        valueIdentifierKey?: keyof O;

        /**
         * The name of the property on each option object that holds the human-readable text label.
         * Once an option is matched using `valueIdentifierKey`, this key is used to retrieve
         * the string that should be displayed in the chip for that selected option
         * (e.g., `matchedOption[valueDisplayKey]`).
         *
         * @default "name"
         * @example valueDisplayKey="title"
         * @example // Given options: [{ id: 'abc', description: 'Item 1' }]
         * valueDisplayKey="description"
         */
        valueDisplayKey?: keyof O;
    };
function useValueDisplay<O extends Option>(
    value: string[] | undefined,
    options: O[] | undefined,
    valueIdentifierKey: keyof O | undefined,
    valueDisplayKey: keyof O | undefined,
): { mainValue: string | null; additionalValuesNumber: number } {
    if (value?.length) {
        const mainValueId = value[0];
        const correspondingOption = options?.find(
            (option) => option[valueIdentifierKey ?? "id"] === mainValueId,
        );
        if (!correspondingOption) {
            console.warn(`Option with id ${mainValueId} not found in options`);
        }
        const mainValue =
            correspondingOption?.[valueDisplayKey ?? "name"] ??
            correspondingOption?.["id"] ??
            mainValueId;

        if (mainValue !== undefined && typeof mainValue !== "string") {
            throw new Error("Main value must be a string");
        }

        const additionalValuesNumber = value.length - 1;
        return { mainValue, additionalValuesNumber };
    }
    return { mainValue: null, additionalValuesNumber: 0 };
}

function useNoOptionsWarning(options: Option[] | undefined) {
    useEffect(() => {
        if (!options) {
            console.warn(
                "You are not providing options to the Dropdown Trigger component," +
                    "so the value displayed in the trigger may not be the same as the actual label in the dropdown.",
            );
        }
    }, [options]);
}

const DropdownChipFilterTrigger = <O extends Option>({
    leadingElement,
    label,
    valueDisplayKey,
    valueIdentifierKey,
    options,
    ...props
}: DropdownChipTriggerProps<O>) => {
    const ctx = useDropdownCtx();

    if (!ctx) {
        throw new Error("DropdownChipTrigger must be used within a Dropdown");
    }

    const { mainValue, additionalValuesNumber } = useValueDisplay(
        ctx.value,
        options,
        valueIdentifierKey,
        valueDisplayKey,
    );

    const displayedValue = useMemo(() => {
        if (!mainValue) {
            return undefined;
        }
        if (additionalValuesNumber === 0) {
            return mainValue;
        }
        return `${mainValue}, +${additionalValuesNumber}`;
    }, [mainValue, additionalValuesNumber]);

    const handleClick = (e: PointerEvent<SVGSVGElement>) => {
        if (!ctx.value?.length) return;
        e.stopPropagation();
        e.preventDefault();
        ctx.onReset?.();
    };

    useNoOptionsWarning(options);

    return (
        <DropdownTrigger {...props} asChild>
            <Chip active={!!ctx.value?.length}>
                {leadingElement}
                {label && <Chip.Label>{label}</Chip.Label>}
                {!!displayedValue && <Chip.Value>{displayedValue}</Chip.Value>}
                <Chip.Icon
                    name={ctx.value?.length ? "close" : "chevronDown"}
                    onClick={handleClick}
                />
            </Chip>
        </DropdownTrigger>
    );
};

/* -------------------------------------------------------------------------------------------------
 * Dropdown
 * -----------------------------------------------------------------------------------------------*/
export type DropdownProps = Popover.PopoverProps &
    SelectProps & {
        onReset?: () => void;
    };

type DropdownComponentType = FC<DropdownProps> & {
    Trigger: typeof DropdownTrigger;
    ChipFilterTrigger: typeof DropdownChipFilterTrigger;
    Content: typeof DropdownContent;
    Item: typeof DropdownItem;
    ItemText: typeof DropdownItemText;
    ItemIcon: typeof DropdownItemIcon;
    SelectedIndicator: typeof DropdownItemSelectedIndicator;
    SelectableItem: typeof DropdownSelectableItem;
    Anchor: typeof DropdownAnchor;
    Checkbox: typeof DropdownCheckbox;
    Reset: typeof DropdownResetItem;
};

export const Dropdown: DropdownComponentType = ({
    closeOnValueChange = true,
    defaultOpen,
    defaultValue,
    onValueChange,
    onOpenChange,
    open: propsOpen,
    selectType,
    value: propsValue,
    children,
    ...props
}) => {
    const [open, setOpen] = useControllableState({
        defaultProp: defaultOpen,
        onChange: onOpenChange,
        prop: propsOpen,
    });

    const { handleSelect, value } = useSelect({
        close: () => setOpen(false),
        closeOnValueChange,
        defaultValue,
        onValueChange,
        selectType,
        value: propsValue,
    });

    const onReset = () => {
        if (props.onReset) {
            return props.onReset();
        }

        return onValueChange?.([]);
    };

    return (
        <DropdownCtx.Provider
            value={{
                handleSelect,
                value,
                onReset,
            }}
        >
            <Popover.Root open={open} onOpenChange={setOpen} {...props}>
                {children}
            </Popover.Root>
        </DropdownCtx.Provider>
    );
};

Dropdown.Trigger = DropdownTrigger;
Dropdown.ChipFilterTrigger = DropdownChipFilterTrigger;
Dropdown.Content = DropdownContent;
Dropdown.Item = DropdownItem;
Dropdown.ItemText = DropdownItemText;
Dropdown.ItemIcon = DropdownItemIcon;
Dropdown.SelectedIndicator = DropdownItemSelectedIndicator;
Dropdown.SelectableItem = DropdownSelectableItem;
Dropdown.Anchor = DropdownAnchor;
Dropdown.Checkbox = DropdownCheckbox;
Dropdown.Reset = DropdownResetItem;

export {
    useDropdownItemIconStyles,
    useDropdownItemStyles,
    useDropdownItemTextStyles,
};
